import React, { useEffect, useState, useRef } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as ReTooltip,
} from "recharts";

import { useNavigate } from "react-router-dom";
import { useParams, Link as RouterLink } from "react-router-dom";

import { lineKeyMap } from "../../consts.js";

import Tooltip from "@mui/material/Tooltip";

import { useTheme } from "@mui/material/styles";
import { BrandContext } from "../../App";

import PageTitle from "../../components/PageTitle/PageTitle";
import DefaultButton from "../../components/simple/DefaultButton";
import BorderCard from "../BorderCard/BorderCard";
import Modal from "../Modal/Modal";
import TranscriptDisplay from "../../components/simple/TranscriptDisplay";
import Recommendations from "./Recommendations";

import VideoAvatar from "../VideoAvatar/VideoAvatar";

import { useAPI } from "../../hooks/useAPI.jsx";

import { AccessTime } from "@mui/icons-material";

import TranscriptShare from "./TranscriptShare";

import {
  CircularProgressbarWithChildren,
  buildStyles,
} from "react-circular-progressbar";

import { Container } from "./styles.js";

const FeedbackCoach = () => {
  const theme = useTheme();
  const { brand, user, authToken, isMobile, setSideMenuOpen } =
    React.useContext(BrandContext);

  const { callAPI } = useAPI();
  const [nextScenarioData, setNextScenarioData] = useState(null);
  const [simulation, setSimulation] = useState(null);
  const [feedbackSimulation, setFeedbackSimulation] = useState(null);
  const [feedbackSimulationError, setFeedbackSimulationError] = useState(null);
  const [modal, setModal] = useState({ type: null, open: false });

  const [transcript, setTranscript] = useState(null);
  const [topRecommendations, setTopRecommendations] = useState(null);

  const { simulation_id } = useParams();
  const navigate = useNavigate();

  const feedbackSimulationStarted = useRef(false);

  const [evaluations, setEvaluations] = useState([]);
  const [pastScoring, setPastScoring] = useState([]);
  const [dataPoints, setDataPoints] = useState([]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;

      // Format the date nicely
      const formattedDate = new Date(data.date).toLocaleDateString();

      // Dynamically get metrics (exclude attempts, date, totalScore if needed)
      const excluded = ["attempts", "date", "totalScore"];
      const metrics = Object.keys(data).filter(
        (key) => !excluded.includes(key)
      );

      return (
        <div
          style={{
            backgroundColor: "#E9EAEB",
            padding: "8px 12px",
            borderRadius: "8px",
            color: "#000",
            minWidth: "150px",
            boxShadow: "0 12px 16px -4px #0A0D12",
          }}
        >
          <div
            style={{ marginBottom: "4px", fontSize: "12px", color: "#962EFF" }}
          >
            Total Score - {data.totalScore}
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: "12px",
              marginBottom: "4px",
              gap: "20px",
            }}
          >
            <span>
              {data.attempts} Attempt{data.attempts > 1 ? "s" : ""}
            </span>
            <span>{formattedDate}</span>
          </div>

          {/* Render dynamic metrics */}
          {/* {metrics.map(metric => (
          <div key={metric} style={{ fontSize: "12px" }}>
            {metric.replace("_", " ")}: {data[metric]}
          </div>
        ))} */}
        </div>
      );
    }

    return null;
  };

  async function startFeedbackSimulation() {
    if (feedbackSimulationStarted.current) {
      return;
    }

    feedbackSimulationStarted.current = true;

    try {
      const feedbackSimData = await callAPI(`start_feedback_simulation`, {
        method: "POST",
        token: authToken,
        body: { simulation_id },
      });
      setFeedbackSimulation(feedbackSimData);
    } catch (error) {
      console.error(error.message);
      feedbackSimulationStarted.current = false; // Reset on error to allow retry
      throw error;
    }
  }

  async function updateSimulationData() {
    try {
      const simData = await callAPI(
        `simulation?simulation_id=${simulation_id}`,
        {
          token: authToken,
        }
      );

      setSimulation(simData);

      const trainingResponse = await callAPI(`trainingprograms`, {
        token: authToken,
      });

      for (const program of trainingResponse) {
        for (const chapter of program.chapters) {
          const scenarios = chapter?.scenarios || [];
          const index = scenarios.findIndex(
            (s) => s.id === simData.scenario.id
          );

          if (index !== -1) {
            const nextScenario = scenarios[index + 1];
            if (nextScenario?.id) {
              const nextScenarioRes = await callAPI(
                `scenario?scenario_id=${nextScenario.id}`,
                {
                  token: authToken,
                }
              );
              setNextScenarioData(nextScenarioRes);
            }
            return;
          }
        }
      }
    } catch (err) {
      console.error(
        "Failed to fetch simulation or next scenario data:",
        err.message
      );
    }
  }

  React.useEffect(() => {
    document.title = `Simulation Feedback - ${brand.name}`;
  }, [user]);

  React.useEffect(() => {
    updateSimulationData();
  }, [authToken]);

  useEffect(() => {
    if (!isMobile) setSideMenuOpen(false);

    return () => {
      if (!isMobile) setSideMenuOpen(true);
    };
  }, []);

  useEffect(() => {
    if (simulation && simulation.evaluation === null) {
      const interval = setInterval(() => {
        console.log("Checking for evaluation update...");
        updateSimulationData();
      }, 5000);

      return () => clearInterval(interval);
    }

    if (simulation?.evaluation) {
      // TODO: When data is like the initial setup of this above
      const _evaluations = Object.entries(simulation.evaluation)
        .filter(([k, v]) => typeof v === "object" && v.label)
        .map(([key, value]) => ({
          key,
          label: value.label,
          value: value.value,
          description: value.description,
        }));

      setEvaluations(_evaluations);
      setTopRecommendations(
        Object.entries(simulation.evaluation)
          .filter(([key, value]) => value.recommendations)
          .map(([_, value]) => value.recommendations[0])
      );

      const fetchTranscript = async () => {
        try {
          const transcriptData = await callAPI(
            `simulation_transcript?simulation_id=${simulation.id}`,
            {
              token: authToken,
            }
          );
          setTranscript(transcriptData);
        } catch (error) {
          console.error(error.message);
        }
      };

      const fetchScores = async () => {
        try {
          const scoresResponse = await callAPI(
            `past_scenario_scores?scenario_id=${simulation?.scenario?.id}`,
            {
              token: authToken,
            }
          );
          setPastScoring(scoresResponse);
        } catch (error) {
          console.error(error.message);
        }
      };

      // fetch data
      fetchScores();
      fetchTranscript();
    }
  }, [simulation]);

  useEffect(() => {
    let _formattedScores = [];
    if (pastScoring && pastScoring?.scores?.length > 0) {
      pastScoring?.scores.map((score, i) => {
        _formattedScores.push({
          attempts: score.attempts,
          totalScore: parseFloat(score.totalScore),
          date: score.date,
          ...score.scores,
        });
      });
      setDataPoints(_formattedScores);
    }
  }, [pastScoring]);

  const CTAButton = () => {
    let returnVal = {
      label: null,
      url: null,
    };
    if (nextScenarioData && nextScenarioData.has_scoring_engine) {
      returnVal.label = "Next Simulation Challenge";
      returnVal.url = `/dashboard/simulate/${nextScenarioData.id}`;
    } else if (nextScenarioData && !nextScenarioData.has_scoring_engine) {
      returnVal.label = "Start Next Skill Session";
      returnVal.url = `/dashboard/simulate/${nextScenarioData.id}`;
    } else {
      returnVal.label = "Back to simulations";
      returnVal.url = `/dashboard/simulations`;
    }
    return returnVal;
  };

  const RecommendationIcon = () => {
    return (
      <span className="icon">
        <svg
          width="14"
          height="8"
          viewBox="0 0 14 8"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12.8333 1.08337L8.24326 5.67341C8.01225 5.90442 7.89674 6.01992 7.76355 6.0632C7.64639 6.10127 7.52019 6.10127 7.40303 6.0632C7.26984 6.01992 7.15434 5.90442 6.92333 5.67341L5.32659 4.07667C5.09558 3.84566 4.98008 3.73016 4.84689 3.68688C4.72973 3.64882 4.60352 3.64882 4.48637 3.68688C4.35317 3.73016 4.23767 3.84566 4.00666 4.07667L1.16663 6.91671M12.8333 1.08337H8.74996M12.8333 1.08337V5.16671"
            stroke="#A87EFF"
            strokeWidth="1.33333"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    );
  };

  const excluded = ["date", "attempts"];
  const metricKeys = dataPoints.length
    ? Object.keys(dataPoints[0]).filter((key) => !excluded.includes(key))
    : [];

  const scoreFormatter = (score) => {
    let _score = score / 10;
    return _score.toFixed(1);
  };

  return (
    <>
      {simulation == null || simulation.evaluation == null ? (
        <Container>
          <div className="gathering-results">
            <PageTitle text="Please wait while we tailor your personalised feedback." />
            <DefaultButton
              wideOnMobile
              onClick={() => navigate(CTAButton().url)}
            >
              {CTAButton().label}
            </DefaultButton>
          </div>
        </Container>
      ) : (
        <Container className="feedback-coach-container">
          <div className="feedback-header">
            <PageTitle
              text={`Take a look below at your scores ${
                simulation?.user?.first_name || ""
              }`}
            >
              Your results break down your strengths and where you can keep
              improving in this simulation challenge.
            </PageTitle>
            <div className="actions">
              <DefaultButton
                onClick={() => navigate("/dashboard/home")}
                backgroundColor="transparent"
                textColor={theme.palette.text.primary}
                borderColor={theme.palette.border?.brand}
                fullWidth
                wideOnMobile
              >
                Return to Dashboard
              </DefaultButton>

              <DefaultButton
                onClick={() => navigate(CTAButton().url)}
                fullWidth
                wideOnMobile
              >
                {CTAButton().label}
              </DefaultButton>
            </div>
          </div>
          <div className="grouped-content-row">
            <BorderCard
              title="Feedback coach avatar"
              subTitle={`Scenario: ${simulation?.scenario?.name || ""}`}
              actions={
                <div className="transcript-features">
                  <DefaultButton
                    onClick={() => setModal({ type: "transcript", open: true })}
                    backgroundColor="transparent"
                    textColor={theme.palette.text.primary}
                    borderColor={theme.palette.border?.brand}
                    fullWidth
                    wideOnMobile
                  >
                    <svg
                      width="13"
                      height="16"
                      viewBox="0 0 16 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M9.66665 9.16663H4.66665M6.33331 12.5H4.66665M11.3333 5.83329H4.66665M14.6666 5.66663V14.3333C14.6666 15.7334 14.6666 16.4335 14.3942 16.9683C14.1545 17.4387 13.772 17.8211 13.3016 18.0608C12.7668 18.3333 12.0668 18.3333 10.6666 18.3333H5.33331C3.93318 18.3333 3.23312 18.3333 2.69834 18.0608C2.22793 17.8211 1.84548 17.4387 1.6058 16.9683C1.33331 16.4335 1.33331 15.7334 1.33331 14.3333V5.66663C1.33331 4.26649 1.33331 3.56643 1.6058 3.03165C1.84548 2.56124 2.22793 2.17879 2.69834 1.93911C3.23312 1.66663 3.93318 1.66663 5.33331 1.66663H10.6666C12.0668 1.66663 12.7668 1.66663 13.3016 1.93911C13.772 2.17879 14.1545 2.56124 14.3942 3.03165C14.6666 3.56643 14.6666 4.26649 14.6666 5.66663Z"
                        stroke="#FEFEFE"
                        strokeWidth="1.66667"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <span>Transcript</span>
                  </DefaultButton>
                  <TranscriptShare
                    user={user}
                    transcriptData={transcript?.transcript}
                    isMobile={isMobile}
                    theme={theme}
                  />
                </div>
              }
            >
              {feedbackSimulationError ? (
                <div className="avatar-unavailable">
                  <AccessTime />
                  <p>Your feedback avatar is coming soon</p>
                </div>
              ) : (
                <VideoAvatar
                  autoConnect={false}
                  scenario={simulation?.scenario}
                  config={feedbackSimulation}
                  isFeedbackSession={true}
                  startFeedbackSimulation={startFeedbackSimulation}
                  setFeedbackSimulationError={setFeedbackSimulationError}
                />
              )}
            </BorderCard>
            <BorderCard
              title="Your results in detail"
              subTitle={`Scenario: ${simulation?.scenario?.name || ""}`}
            >
              <div className="progress-wheel">
                <CircularProgressbarWithChildren
                  value={50}
                  strokeWidth={12.5}
                  styles={buildStyles({
                    rotation: 0.75,
                    strokeLinecap: "round",
                    pathColor: theme.palette.border.brand,
                    trailColor: theme.palette.border.secondary,
                    textColor: theme.palette.text.primary,
                    pathTransition: "stroke-dashoffset 0.5s ease 0s",
                  })}
                >
                  <div className="progress-label">
                    <span>Total Score</span>
                    {scoreFormatter(simulation.evaluation.total_score.value)}
                  </div>
                </CircularProgressbarWithChildren>
              </div>
              <div className="scoring-factors">
                <h3>Scoring Breakdown</h3>
                <ul>
                  {evaluations &&
                    evaluations.map((item, i) => {
                      if (item.key === "total_score") return;
                      if (item.description) {
                        return (
                          <li key={i}>
                            <Tooltip
                              title={item.description}
                              placement="top"
                              slotProps={{
                                tooltip: {
                                  sx: {
                                    color: "#02002B",
                                    background: "#D1C7FF",
                                    "& span:before": {
                                      background: "#D1C7FF",
                                    },
                                  },
                                },
                              }}
                              arrow
                            >
                              <span>
                                {item.label}{" "}
                                <span>{scoreFormatter(item.value)}</span>
                              </span>
                            </Tooltip>
                          </li>
                        );
                      } else {
                        return (
                          <li key={i}>
                            <span>
                              {item.label}{" "}
                              <span>{scoreFormatter(item.value)}</span>
                            </span>
                          </li>
                        );
                      }
                    })}
                </ul>
              </div>
            </BorderCard>
          </div>
          <div
            className="grouped-content-row"
            style={{ display: isMobile ? "none" : "flex" }}
          >
            <BorderCard
              title="See how you're improving each time"
              subTitle={`Scenario: ${simulation?.scenario?.name || ""}`}
            >
              <div className="chart-results">
                <div className="key-explanation">
                  <p>
                    Watch how your overall and individual scores shift with each
                    try of this simulation challenge.
                  </p>
                  <div className="key">
                    <ul>
                      {evaluations.map((item, i) => {
                        if (item.key === "total_score") return;
                        return (
                          <li key={i} className={item.key}>
                            <span
                              style={{
                                backgroundColor: lineKeyMap[item?.key]?.color,
                              }}
                            ></span>{" "}
                            {item.label}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
                <div className="chart-wrapper">
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={dataPoints}>
                      <CartesianGrid
                        stroke={theme.palette.border.tertiary}
                        strokeDasharray="3 3"
                      />

                      <XAxis
                        dataKey="attempts"
                        height={60}
                        axisLine={false}
                        tickLine={false}
                        tick={{
                          fill: theme.palette.text.tertiary,
                          style: { fontSize: theme.typography.textXS.fontSize },
                          dy: 5,
                        }}
                        label={{
                          value: "Number of Attempts",
                          dy: 30,
                          fill: theme.palette.text.tertiary,
                          fontSize: theme.typography.textXS.fontSize,
                          fontWeight: theme.typography.fontWeightRegular,
                        }}
                      />

                      <YAxis
                        label={{
                          value: "Score",
                          angle: -90,
                          position: "insideLeft",
                          fill: theme.palette.text.tertiary,
                          fontSize: theme.typography.textXS.fontSize,
                          fontWeight: theme.typography.fontWeightRegular,
                        }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={(value) => value.toFixed(1)}
                        domain={["auto", "auto"]}
                      />

                      <ReTooltip content={CustomTooltip} />

                      {/* DYNAMIC LINES */}
                      {metricKeys.map((metric, i) => (
                        <Line
                          key={metric}
                          type="monotone"
                          dataKey={metric}
                          stroke={lineKeyMap[metric]?.color} // alternating colors
                          name={metric.replace("_", " ")}
                          strokeWidth={2}
                          strokeDasharray={
                            metric !== "totalScore" ? "5 5" : "0"
                          }
                          dot={false}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </BorderCard>
            <BorderCard
              title="Recommendations"
              subTitle={`Scenario: ${simulation?.scenario?.name || ""}`}
            >
              <div className="recommendations">
                <h3>
                  Next time try focusing on these points to increase your score
                  even more 💪
                </h3>
                <ul>
                  {topRecommendations &&
                    topRecommendations.map((item, i) => (
                      <li key={i}>
                        <RecommendationIcon />
                        <span>{item}</span>
                      </li>
                    ))}
                </ul>
                <div className="actions">
                  <DefaultButton
                    onClick={() =>
                      setModal({ type: "recommendations", open: true })
                    }
                    backgroundColor="transparent"
                    textColor={theme.palette.text.primary}
                    borderColor={theme.palette.border?.brand}
                    fullWidth
                    wideOnMobile
                  >
                    View all Recommendations
                  </DefaultButton>

                  <DefaultButton
                    onClick={() => navigate(CTAButton().url)}
                    fullWidth
                    wideOnMobile
                  >
                    {nextScenarioData
                      ? `Next up: ${nextScenarioData.name}`
                      : "Back to simulations"}
                  </DefaultButton>
                </div>
              </div>
            </BorderCard>
          </div>
        </Container>
      )}
      {modal.open && modal.type === "transcript" && (
        <Modal
          title="Transcript"
          subTitle={`${simulation?.scenario?.name || ""}`}
          open={modal.open}
          onClose={() => setModal({ type: null, open: false })}
          showIcon
          icon={
            <svg
              width="24"
              height="25"
              viewBox="0 0 24 25"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M14 11.5H8M10 15.5H8M16 7.5H8M20 7.3V17.7C20 19.3802 20 20.2202 19.673 20.862C19.3854 21.4265 18.9265 21.8854 18.362 22.173C17.7202 22.5 16.8802 22.5 15.2 22.5H8.8C7.11984 22.5 6.27976 22.5 5.63803 22.173C5.07354 21.8854 4.6146 21.4265 4.32698 20.862C4 20.2202 4 19.3802 4 17.7V7.3C4 5.61984 4 4.77976 4.32698 4.13803C4.6146 3.57354 5.07354 3.1146 5.63803 2.82698C6.27976 2.5 7.11984 2.5 8.8 2.5H15.2C16.8802 2.5 17.7202 2.5 18.362 2.82698C18.9265 3.1146 19.3854 3.57354 19.673 4.13803C20 4.77976 20 5.61984 20 7.3Z"
                stroke="#211F45"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          }
        >
          <TranscriptDisplay
            transcript={transcript?.transcript}
            scenarioData={simulation?.scenario}
          />
        </Modal>
      )}
      {modal.open && modal.type === "recommendations" && (
        <Modal
          title="All Recommendations"
          subTitle={`${simulation?.scenario?.name || ""}`}
          open={modal.open}
          onClose={() => setModal({ type: null, open: false })}
          showIcon
          icon={
            <svg
              width="24"
              height="25"
              viewBox="0 0 24 25"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7 22.5V11.5M2 13.5V20.5C2 21.6046 2.89543 22.5 4 22.5H17.4262C18.907 22.5 20.1662 21.4197 20.3914 19.9562L21.4683 12.9562C21.7479 11.1389 20.3418 9.5 18.5032 9.5H15C14.4477 9.5 14 9.05228 14 8.5V4.96584C14 3.60399 12.896 2.5 11.5342 2.5C11.2093 2.5 10.915 2.6913 10.7831 2.98812L7.26394 10.9061C7.10344 11.2673 6.74532 11.5 6.35013 11.5H4C2.89543 11.5 2 12.3954 2 13.5Z"
                stroke="#211F45"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          }
          isWide
        >
          <Recommendations simulation={simulation} isMobile={isMobile} />
        </Modal>
      )}
    </>
  );
};

export default FeedbackCoach;
